"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.MarketSelector = MarketSelector;
exports.default = MarketSelectorWrap;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _react = _interopRequireWildcard(require("react"));
var _componentUtilities = require("@ux/component-utilities");
var _propTypes = _interopRequireDefault(require("prop-types"));
var _reactMintl = require("@godaddy/react-mintl");
var _link = _interopRequireDefault(require("@ux/link"));
var _columnLayout = require("@ux/column-layout");
var _trayMenu = require("@ux/tray-menu");
var _util = require("@ux/util");
var _golfToEid = _interopRequireDefault(require("@gd-internal/golf-to-eid"));
var _world = _interopRequireDefault(require("@ux/icon/world"));
var _mapPin = _interopRequireDefault(require("@ux/icon/map-pin"));
var _atlas = require("@godaddy/atlas");
var _utils = require("./utils");
require("@ux/tray-menu/styles");
require("@ux/column-layout/styles");
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const reDescSplit = /\s?-\s?/;

/**
 * Market selector component.
 *
 * This renders an anchor with the label showing the current market. When it is
 * clicked it will open up a tray dropdown with all the available markets.
 * Clicking on them will then fire of a transfer to the market website.
 * @param {Object} props The props for this component
 * @returns {React.ReactElement} JSX for market selector
 * @public
 */
// eslint-disable-next-line max-statements
function MarketSelector(props) {
  const [trayMenu, setTrayMenu] = (0, _react.useState)(null);
  const [brand, setBrand] = (0, _react.useState)(null);
  (0, _react.useEffect)(() => {
    const atlasEnv = (0, _utils.getAtlasEnv)(props.env);
    const builder = _atlas.Atlas.builder(atlasEnv);
    builder.build().then(atlas => {
      setBrand(atlas.resolveBrandByPlid(props.privateLabelId));
    });
  }, [props.env, props.privateLabelId]);

  /**
   * Return the market for the aria-label.
   * Ex: en-US becomes "United States - English"
   *
   * @param {String} marketLocale market
   * @returns {String} Label for the specified market
   * @private
   */
  const getMarketDescription = marketLocale => {
    if (!(0, _utils.isBrand)(brand)) {
      return marketLocale;
    }
    const market = brand.resolveMarketByLocale(marketLocale);
    return market.description;
  };

  /**
   * If we are on a mobile breakpoint, then invoke the handlers to potentially
   * lock or unlock scrolling for the `document.body`.
   *
   * @param  {Boolean} open Value indicating if the TrayMenu in this Component is open.
   */
  const onTrayToggle = open => {
    if (!(0, _componentUtilities.isMobile)(props.breakpoint)) {
      return;
    }
    if (open) {
      _utils.lockUtils.lockScroll(props);
    }
    if (!open) {
      _utils.lockUtils.unlockScroll();
    }
  };

  /**
   * Render the markets into columns.
   *
   * @returns {Array} markets Array of ColumnItems
   * @private
   */
  const renderMarkets = () => {
    const {
      manifest,
      eidPrefix,
      currentMarket,
      urls = {},
      urlArgs
    } = props;
    const markets = (0, _utils.sortMarkets)((0, _utils.isBrand)(brand) ? brand.getMarkets() : []);

    // allow custom redirect urls to be passed in via props
    urls.markets = urls.markets || {};
    return markets.reduce((acc, market) => {
      const {
        marketLocale,
        description
      } = market;
      const eid = (0, _golfToEid.default)(`:${marketLocale.replace(/-/g, '_')}`, {
        prefix: `${eidPrefix}.${(0, _golfToEid.default)(manifest)}.market_selector`,
        postfix: 'click'
      });
      const [countrylabel, langLabel] = description.split(reDescSplit);
      const ariaLabelMarket = countrylabel + ', ' + langLabel;
      let url = urls.markets[marketLocale];
      if (!url) {
        const site = brand.findSiteByLocale(marketLocale);
        if (site) {
          url = `https://${site.url}`;
        }
      }
      url ??= '#';
      if (typeof url === 'string') {
        url = {
          href: url
        };
      }

      // During client re-render in reseller sales header, include explicit
      // target parameter specifying current location.
      if (typeof document !== 'undefined') {
        url = new _util.URL(url.href, true);
        url.set('query', _objectSpread(_objectSpread({}, url.query), {}, {
          target: window.location.href
        }));
        url = {
          href: url.href
        };
      }
      if (market) {
        acc.push(/*#__PURE__*/_react.default.createElement(_columnLayout.ColumnItem, {
          key: marketLocale,
          isSelected: marketLocale === currentMarket
        }, /*#__PURE__*/_react.default.createElement(_link.default, (0, _extends2.default)({}, url, {
          "data-tcc-ignore": true,
          urlArgs: urlArgs,
          "data-market": marketLocale,
          "data-eid": eid,
          "aria-label": ariaLabelMarket
        }), /*#__PURE__*/_react.default.createElement("span", {
          "data-tcc-ignore": true
        }, /*#__PURE__*/_react.default.createElement("strong", {
          "data-tcc-ignore": true
        }, countrylabel), " - ", langLabel, /*#__PURE__*/_react.default.createElement("span", {
          className: "market-location"
        }, /*#__PURE__*/_react.default.createElement(_mapPin.default, {
          height: 14,
          width: 14
        }))))));
      }
      return acc;
    }, []);
  };

  /**
   * Get the two-letter short language code for the specified market
   * e.g. en-US: EN, el-GR: EL, fr-CA: FR
   *
   * If a displayCode is returned from the Markets API, use that instead.
   *
   * @param {String} marketLocale The market name to parse (e.g. en-US, el-GR, fr-CA)
   * @returns {String} The language code of the specified market, in upper case
   * @private
   */
  const getShortLanguageCode = marketLocale => {
    if (!marketLocale || typeof marketLocale !== 'string') return '';
    const marketParts = marketLocale.split(reDescSplit);
    return marketParts[0].toUpperCase();
  };

  /**
   * Generate the tray title to show
   *
   * @returns {ReactElement} The title node to show for the tray
   * @private
   */
  const getTrayName = () => {
    const {
      currentMarket,
      compact
    } = props;
    const currentMarketName = getMarketDescription(currentMarket);
    let label = currentMarketName;
    if (compact) {
      label = getShortLanguageCode(currentMarket);
    }
    return /*#__PURE__*/_react.default.createElement("span", {
      className: "mrkt-selector-tray-name",
      title: currentMarketName
    }, /*#__PURE__*/_react.default.createElement(_world.default, null), /*#__PURE__*/_react.default.createElement("span", {
      className: "mrkt-selector-tray-label"
    }, label));
  };
  const {
    numColumns,
    messages,
    currentMarket,
    eidPrefix,
    manifest
  } = props;
  const eid = (0, _golfToEid.default)('market_selector', {
    prefix: `${eidPrefix}.${(0, _golfToEid.default)(manifest)}`,
    postfix: 'click'
  });
  const marketDescription = getMarketDescription(currentMarket);
  if (trayMenu) {
    const isOpen = trayMenu.state.open;
    if ((0, _componentUtilities.isMobile)(props.breakpoint) && isOpen) {
      _utils.lockUtils.lockScroll(props);
    } else if ((0, _componentUtilities.isMobile)(props.breakpoint) && !isOpen || !(0, _componentUtilities.isMobile)(props.breakpoint) && isOpen) {
      _utils.lockUtils.unlockScroll();
    }
  }
  return /*#__PURE__*/_react.default.createElement(_reactMintl.IntlProvider, {
    locale: currentMarket,
    messages: messages
  }, /*#__PURE__*/_react.default.createElement(_reactMintl.FormattedMessage, {
    id: "Shared:UtilityBar:MarketSelectorTray:AriaLabel",
    values: {
      selection: marketDescription
    }
  }, content => /*#__PURE__*/_react.default.createElement(_trayMenu.TrayMenu, {
    ariaLabel: content.selection,
    type: "base"
    // eslint-disable-line no-return-assign
    ,
    ref: c => setTrayMenu(c),
    name: getTrayName(),
    className: "market-selector",
    onToggle: onTrayToggle,
    "data-eid": eid
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "tray-content-wrap"
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "tray-content-flex market-selector-content"
  }, /*#__PURE__*/_react.default.createElement(_trayMenu.TrayTitle, null, /*#__PURE__*/_react.default.createElement(_reactMintl.FormattedMessage, {
    id: "Shared:UtilityBar:MarketSelectorTray:Title"
  })), /*#__PURE__*/_react.default.createElement(_columnLayout.Columns, {
    num: numColumns,
    style: "vertical",
    className: "market-list"
  }, renderMarkets()))))));
}

/**
 * Property definitions.
 *
 * @type {Object}
 * @public
 */
MarketSelector.propTypes = {
  manifest: _propTypes.default.string,
  eidPrefix: _propTypes.default.string,
  currentMarket: _propTypes.default.string.isRequired,
  intl: _propTypes.default.object,
  numColumns: _propTypes.default.number,
  urls: _propTypes.default.object,
  urlArgs: _propTypes.default.object,
  messages: _propTypes.default.object.isRequired,
  compact: _propTypes.default.bool,
  open: _propTypes.default.bool,
  lockScrollOnOpen: _propTypes.default.bool,
  privateLabelId: _propTypes.default.number,
  breakpoint: _propTypes.default.string,
  /* Atlas environment - defaults to env from hostname */
  env: _propTypes.default.string
};

/**
 * Default property values.
 *
 * @type {Object}
 * @public
 */
MarketSelector.defaultProps = {
  manifest: 'SalesHeader',
  eidPrefix: 'uxp.hyd',
  currentMarket: 'en-US',
  numColumns: 4,
  urlArgs: {},
  compact: false,
  open: false,
  lockScrollOnOpen: false,
  privateLabelId: 1
};
function MarketSelectorWrap(props) {
  const {
    breakpoint
  } = (0, _componentUtilities.useBreakpoints)('mobile', 'phablet', 'tablet', 'desktop', 'large');
  return /*#__PURE__*/_react.default.createElement(MarketSelector, (0, _extends2.default)({}, props, {
    breakpoint: breakpoint
  }));
}
MarketSelectorWrap.propTypes = {};